import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { useConfirmation } from '../shared/ZCard';
import { Check, X, Plus, Minus } from 'lucide-react';
import OverflowMenu from '../shared/OverflowMenu';
import ZLoadingBar from '../shared/ZLoadingBar';
import WorldMap from './WorldMap';
import CountryList from './CountryList';

const EMPTY_ADD = { name: '', code: '', visits: 1 };

/**
 * GiraffeCountriesTab — Countries tab inside the Giraffe root four-tab layout.
 *
 * Extracted from the standalone GiraffeCountries.js page (now deprecated).
 * Identical rendering and edit behavior; no header (the parent Giraffe.js
 * provides the app header), no breadcrumb (Countries is a root-level tab,
 * not a nested view).
 */
export default function GiraffeCountriesTab() {
  const [countriesData, setCountriesData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addingTo, setAddingTo] = useState(null);
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const feedback = useConfirmation();

  useEffect(() => {
    api.get('/giraffe/countries')
      .then(({ data }) => setCountriesData(data))
      .catch(() => {});
  }, []);

  const activeData = editing ? draft : countriesData;

  const visitedCodes = useMemo(() => {
    if (!activeData) return new Set();
    const codes = new Set();
    activeData.continents.forEach(c =>
      c.countries.forEach(cc => codes.add(cc.code))
    );
    return codes;
  }, [activeData]);

  const visitCounts = useMemo(() => {
    if (!activeData) return {};
    const counts = {};
    activeData.continents.forEach(c =>
      c.countries.forEach(cc => { counts[cc.code] = cc.visits; })
    );
    return counts;
  }, [activeData]);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(countriesData)));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(null);
    setAddingTo(null);
    setAddForm(EMPTY_ADD);
  };

  const togglePublic = async () => {
    const newVal = !countriesData.is_public;
    const updated = { ...countriesData, is_public: newVal };
    try {
      const { data } = await api.put('/giraffe/countries', updated);
      setCountriesData(data);
      feedback.confirm();
    } catch {
      feedback.fail();
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/giraffe/countries', draft);
      setCountriesData(data);
      setEditing(false);
      setDraft(null);
      setAddingTo(null);
      setAddForm(EMPTY_ADD);
      feedback.confirm();
    } catch {
      feedback.fail();
    } finally {
      setSaving(false);
    }
  };

  const adjustVisits = (ci, cj, delta) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const current = next.continents[ci].countries[cj].visits;
      next.continents[ci].countries[cj].visits = Math.max(1, current + delta);
      return next;
    });
  };

  const removeCountry = (ci, cj) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.continents[ci].countries.splice(cj, 1);
      return next;
    });
  };

  const confirmAdd = (ci) => {
    const name = addForm.name.trim();
    const code = addForm.code.trim().toUpperCase();
    if (!name || !code) return;
    if (code.length !== 2) return;
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.continents[ci].countries.push({ name, code, visits: Number(addForm.visits) || 1 });
      return next;
    });
    setAddingTo(null);
    setAddForm(EMPTY_ADD);
  };

  if (!activeData) {
    return (
      <div style={{ padding: '48px 24px' }}>
        <ZLoadingBar accent="#10B981" loading={true} />
      </div>
    );
  }

  return (
    <div>
      {/* Public toggle in the top-right of the tab content */}
      {countriesData && !editing && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <OverflowMenu
            publishToggle={{ isPublished: !!countriesData.is_public, onToggle: togglePublic }}
            items={[]}
          />
        </div>
      )}

      {/* Map */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '0.5px solid rgba(255,255,255,0.06)',
        borderRadius: 14,
        overflow: 'hidden',
        position: 'relative',
      }}>
        <div style={{ transform: 'scale(1.18) translateY(-6%)', transformOrigin: '50% 42%' }}>
          <WorldMap visitedCodes={visitedCodes} visitCounts={visitCounts} />
        </div>
        <div style={{ position: 'absolute', bottom: 18, right: 22, pointerEvents: 'none' }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 36, fontWeight: 900, color: '#10B981', opacity: 0.85, textShadow: '0 2px 16px rgba(0,0,0,0.8)' }}>
            {visitedCodes.size}
          </span>
        </div>
      </div>

      {!editing && <CountryList continents={countriesData.continents} onEdit={startEdit} />}

      {editing && (
        <div style={{ marginTop: 24 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: 16
          }}>
            <span style={{
              fontSize: 10, color: '#454860',
              letterSpacing: '0.1em', textTransform: 'uppercase',
              fontFamily: 'monospace'
            }}>
              {visitedCodes.size} countries · 5 continents
            </span>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={cancelEdit} style={btnStyle('#454860')}>
                <X size={11} /> Cancel
              </button>
              <button onClick={saveEdit} disabled={saving} style={btnStyle('#10B981')}>
                <Check size={11} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {draft.continents.map((continent, ci) => (
            <div key={continent.name} style={{ marginBottom: 24 }}>
              <div style={{
                fontSize: 9, color: '#454860',
                letterSpacing: '0.12em', textTransform: 'uppercase',
                fontFamily: 'monospace', marginBottom: 10,
                paddingBottom: 6, borderBottom: '0.5px solid rgba(255,255,255,0.06)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span>{continent.name} · {continent.countries.length}</span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {continent.countries.map((country, cj) => (
                  <div key={`${country.code}-${cj}`} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '4px 8px',
                    borderRadius: 20,
                    background: 'rgba(255,255,255,0.03)',
                    border: '0.5px solid rgba(255,255,255,0.10)',
                    fontSize: 11,
                    color: country.visits > 1 ? '#10B981' : '#b0b4c0',
                    fontFamily: 'system-ui',
                  }}>
                    <span>{country.name}</span>
                    <button onClick={() => adjustVisits(ci, cj, -1)} style={countBtnStyle} title="fewer visits">
                      <Minus size={8} />
                    </button>
                    <span style={{ fontSize: 9, fontFamily: 'monospace', color: '#10B981', opacity: 0.8, minWidth: 14, textAlign: 'center' }}>
                      ×{country.visits}
                    </span>
                    <button onClick={() => adjustVisits(ci, cj, +1)} style={countBtnStyle} title="more visits">
                      <Plus size={8} />
                    </button>
                    <button onClick={() => removeCountry(ci, cj)} style={{ ...countBtnStyle, color: '#6b7084', marginLeft: 2 }} title="remove">
                      <X size={9} />
                    </button>
                  </div>
                ))}

                {addingTo === ci ? (
                  <AddRow
                    form={addForm}
                    onChange={setAddForm}
                    onConfirm={() => confirmAdd(ci)}
                    onCancel={() => { setAddingTo(null); setAddForm(EMPTY_ADD); }}
                  />
                ) : (
                  <button
                    onClick={() => { setAddingTo(ci); setAddForm(EMPTY_ADD); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', borderRadius: 20,
                      background: 'transparent',
                      border: '0.5px dashed rgba(255,255,255,0.10)',
                      fontSize: 10, color: '#454860',
                      fontFamily: 'monospace', cursor: 'pointer',
                      letterSpacing: '0.05em',
                    }}
                  >
                    <Plus size={9} /> Add
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddRow({ form, onChange, onConfirm, onCancel }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 5,
      padding: '4px 8px', borderRadius: 20,
      background: 'rgba(255,255,255,0.05)',
      border: '0.5px solid rgba(255,255,255,0.10)',
    }}>
      <input
        autoFocus
        placeholder="Country name"
        value={form.name}
        onChange={e => onChange(f => ({ ...f, name: e.target.value }))}
        onKeyDown={e => e.key === 'Enter' && onConfirm()}
        style={inputStyle(120)}
      />
      <input
        placeholder="XX"
        value={form.code}
        maxLength={2}
        onChange={e => onChange(f => ({ ...f, code: e.target.value }))}
        onKeyDown={e => e.key === 'Enter' && onConfirm()}
        style={inputStyle(28)}
        title="ISO alpha-2 code (e.g. FR)"
      />
      <input
        type="number"
        min={1}
        value={form.visits}
        onChange={e => onChange(f => ({ ...f, visits: e.target.value }))}
        onKeyDown={e => e.key === 'Enter' && onConfirm()}
        style={inputStyle(32)}
        title="Number of visits"
      />
      <button onClick={onConfirm} style={{ ...countBtnStyle, color: '#10B981' }}><Check size={10} /></button>
      <button onClick={onCancel}  style={{ ...countBtnStyle, color: '#6b7084' }}><X size={9} /></button>
    </div>
  );
}

const btnStyle = (color) => ({
  display: 'flex', alignItems: 'center', gap: 5,
  background: 'none', border: 'none', cursor: 'pointer',
  color, fontSize: 10, letterSpacing: '0.1em',
  textTransform: 'uppercase', fontFamily: 'monospace', padding: 0,
});

const countBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: '#6b7084', padding: 1, display: 'flex', alignItems: 'center',
  lineHeight: 1,
};

const inputStyle = (width) => ({
  width, background: 'transparent', border: 'none', outline: 'none',
  color: '#f0f0f5', fontSize: 11, fontFamily: 'system-ui',
  padding: 0,
});
